import { Salesperson as CoreSalesperson } from '@vendasta/salesperson';
import { Salesperson as SalesSalesperson } from '@vendasta/sales';

export interface GetSalesPersonApiResponse {
  salesPersonId: string;
  department: string;
  coverPageTitle: string;
  jobTitle: string;
  isSalesManagerFlag: boolean;
  firstName: string;
  lastName: string;
  supervisor: string;
  email: string;
  phoneNumber: string[];
  pid: string;
  marketId: string;
  address: string;
  city: string;
  state: string;
  country: string;
  region: string;
  zip: string;
  photoUrl: string;
  photoUrlSecure: string;
  lastLogin: string;
  created: string;
  updated: string;
  salesTeamIds: string[];
}

export class Salesperson {
  public fullName: string;
  public id: string;
  public partnerId?: string;
  public marketId?: string;
  public email?: string;
  public phoneNumbers?: string[];
  public lastLogin?: Date;
  public pictureServingUrl: string;
  public jobTitle?: string;
  public teamIds?: string[];
  public unifiedUserId?: string;

  public static createSalespeopleFromGetResponse(rawSalespeople: GetSalesPersonApiResponse[]): Salesperson[] {
    const result = rawSalespeople.map((sp) => {
      return {
        fullName: `${sp.firstName} ${sp.lastName}`,
        id: sp.salesPersonId,
        partnerId: sp.pid,
        marketId: sp.marketId,
        email: sp.email,
        phoneNumbers: sp.phoneNumber !== null ? sp.phoneNumber : [],
        lastLogin: new Date(sp.lastLogin),
        pictureServingUrl: sp.photoUrlSecure,
        jobTitle: sp.jobTitle,
        teamIds: sp.salesTeamIds !== null ? sp.salesTeamIds : [],
      };
    });
    return result;
  }

  public static convertSalespeopleFromSalesResponse(rawSalespeople: SalesSalesperson[]): Salesperson[] {
    return rawSalespeople.map((sp) => {
      return <Salesperson>{
        fullName: `${sp.firstName} ${sp.lastName}`,
        id: sp.salespersonId,
        marketId: sp.marketId,
        email: sp.email,
        phoneNumbers: sp.phoneNumber !== null ? sp.phoneNumber : [],
        lastLogin: new Date(sp.lastLogin),
        pictureServingUrl: sp.photoUrlHttps,
        unifiedUserId: sp.userId,
      };
    });
  }

  public static createSalespeople(rawSalespeople: any, partnerId?: string, marketId?: string): Salesperson[] {
    const salespeople: Salesperson[] = [];
    rawSalespeople.forEach(function (rawSalesperson: any): void {
      const salesperson = new Salesperson();
      salesperson.fullName = rawSalesperson.firstName + ' ' + rawSalesperson.lastName;
      salesperson.id = rawSalesperson.salesPersonId;
      salesperson.partnerId = rawSalesperson.pid || partnerId;
      salesperson.marketId = rawSalesperson.marketId || marketId;
      salesperson.email = rawSalesperson.email;
      salesperson.phoneNumbers = rawSalesperson.phoneNumber;
      salesperson.pictureServingUrl = rawSalesperson.photoUrlSecure;
      salesperson.jobTitle = rawSalesperson.jobTitle;
      salesperson.lastLogin = rawSalesperson.lastLogin
        ? new Date(rawSalesperson.last_login_timestamp * 1000)
        : undefined;
      salesperson.teamIds = rawSalesperson.salesTeamIds;
      salespeople.push(salesperson);
    });
    return salespeople;
  }

  public static createSTSalespeople(rawSalespeople: any, partnerId?: string, marketId?: string): Salesperson[] {
    const salespeople: Salesperson[] = [];
    rawSalespeople.forEach(function (rawSalesperson: any): void {
      const salesperson = new Salesperson();
      salesperson.fullName = rawSalesperson.sales_person_name;
      salesperson.id = rawSalesperson.sales_person_id;
      salesperson.partnerId = rawSalesperson.partner_id || partnerId;
      salesperson.marketId = rawSalesperson.market_id || marketId;
      salesperson.email = rawSalesperson.email;
      salesperson.phoneNumbers = rawSalesperson.phone_numbers;
      salesperson.pictureServingUrl = rawSalesperson.picture_serving_url;
      salesperson.jobTitle = rawSalesperson.jobTitle;
      salesperson.lastLogin = rawSalesperson.last_login_timestamp
        ? new Date(rawSalesperson.last_login_timestamp * 1000)
        : undefined;
      salesperson.teamIds = rawSalesperson.salesTeamIds;
      salespeople.push(salesperson);
    });
    return salespeople;
  }

  public static fromCoreSalesperson(sp: CoreSalesperson): Salesperson {
    const salesperson = new Salesperson();
    salesperson.fullName = sp.fullName;
    salesperson.id = sp.salespersonId;
    salesperson.partnerId = sp.partnerId;
    salesperson.marketId = sp.marketId;
    salesperson.email = sp.email;
    salesperson.phoneNumbers = [];
    return salesperson;
  }

  public static toCoreSalesperson(sp: Salesperson): CoreSalesperson {
    return <CoreSalesperson>{
      salespersonId: sp.id,
      fullName: sp.fullName,
      partnerId: sp.partnerId,
      marketId: sp.marketId,
      email: sp.email,
    };
  }
}
